import { lazy, Suspense, useEffect } from 'react';

import { useUnit } from 'effector-react';

import AppAlert from '@m/components/AppAlert';
import BottomAppAlert from '@m/components/BottomAppAlert';
import { useCustomerFeatureFlag } from '@m/hooks/data/featureFlags';
import { useHeapIdentifyUser } from '@m/hooks/useHeapIdentifyUser';
import useVisitPolling from '@m/hooks/useVisitPolling';
import { qrcodeStore } from '@m/state/qrcode';
import { userAnalytics } from '@m/utils/userAnalytics';
import { useClosestMetropolisRegion } from 'apps/customer/hooks/useClosestMetropolisRegion';
import { useInitialDataForUser } from 'apps/customer/hooks/useInitialDataForUser';
import { useRefreshTodoItems } from 'apps/customer/hooks/useRefreshTodoItems';
import { useUserInitialization } from 'apps/customer/hooks/useUserInitialization';
import CustomerRoutes from 'apps/customer/routes/CustomerRoutes';
import { todoStore } from 'apps/customer/state/todo';
import { uiStore } from 'apps/customer/state/ui';
import { userStore } from 'apps/customer/state/user';
import { fetchTipSettings, visitStore } from 'apps/customer/state/visit';
import { isRmeStatusInReview } from 'apps/customer/utils/visit';

import useDoesUserHaveSubscriptions from './hooks/useDoesUserHaveSubscriptions';
import useIsFirstTimeUser from './hooks/useIsFirstTimeUser';
import { EMBEDDED_PLACEMENT_IDS } from './iterable/embedded-placements';
import { useEmbeddedSync } from './iterable/useEmbeddedSync';
import { useInAppMessages } from './iterable/useInAppMessages';
import { useIterable } from './iterable/useIterable';

import type { User } from '@m/types/api';

const SideMenu = lazy(() => import('apps/customer/components/SideMenu'));
const AppFullModal = lazy(() => import('apps/customer/components/AppFullModal'));
const UserAgreementSheet = lazy(() => import('apps/customer/components/AgreementSheet'));

function Customer() {
  const { isAppModalVisible } = useUnit(uiStore);
  const { expressOnboardingExperimentGroup } = useUnit(todoStore);

  const {
    user,
    status: { parseInvitationCodeSuccess, getUserSuccess },
    authenticated,
  } = useUnit(userStore);

  const { mostRecentVisit } = useUnit(visitStore);

  const {
    status: { handleQRCodeSuccess },
  } = useUnit(qrcodeStore);

  const { id: visitId, open: visitOpen, rmeStatus } = mostRecentVisit || {};
  const shouldShowVisitMenuItem = visitOpen && !isRmeStatusInReview(rmeStatus);

  /* hide initial loader */
  useEffect(() => {
    if (
      authenticated !== null ||
      getUserSuccess !== null ||
      parseInvitationCodeSuccess !== null ||
      handleQRCodeSuccess !== null
    ) {
      document.getElementById('initial-loader')?.remove();
    }
  }, [authenticated, getUserSuccess, parseInvitationCodeSuccess, handleQRCodeSuccess]);

  useEffect(() => {
    if (authenticated && user) {
      userAnalytics.setUser(user as User);
    }
  }, [authenticated, user]);

  /* Init data */
  useUserInitialization();
  useRefreshTodoItems();
  const userDataInitialized = useInitialDataForUser(user?.id);
  useVisitPolling({ interval: 4000, enabled: !!authenticated });

  /* Handle region to display */
  useClosestMetropolisRegion();

  /* Heap tracking */
  useHeapIdentifyUser({
    authenticated,
    // TODO: fix userStore user type
    user: user as User,
  });

  /* Init Heap first-time-user  hook */
  useIsFirstTimeUser();
  useDoesUserHaveSubscriptions();

  /* Iterable — admin feature flags (evaluated via customer `/customer/feature/:name` API) */
  const { data: iterableCustomerAppMessageEnabled = false } = useCustomerFeatureFlag(
    'IterableCustomerAppMessage',
  );
  const { data: iterableCustomerAppEmbeddedMessagesEnabled = false } = useCustomerFeatureFlag(
    'IterableCustomerAppEmbeddedMessages',
  );

  /* Initialize Iterable SDK — userStore.user is still typed as User | empty object until TODO in store */
  const iterableUser = user && typeof user === 'object' && 'id' in user ? (user as User) : null;

  const { isInitialized: isIterableInitialized } = useIterable({
    authenticated,
    enabled: iterableCustomerAppMessageEnabled,
    user: iterableUser,
  });

  /* In-app messages — SDK handles display and tracking automatically */
  useInAppMessages({ authenticated, isInitialized: isIterableInitialized });

  /* Embedded messages — single fetch on app load (EN-11780). Placement
     components subscribe via `useEmbeddedMessages`. Mounting the placements
     onto the dashboard / offer-feed surfaces is owned by those page owners. */
  useEmbeddedSync({
    isInitialized: isIterableInitialized && iterableCustomerAppEmbeddedMessagesEnabled,
    placementIds: EMBEDDED_PLACEMENT_IDS,
  });

  /* Fetch visit settings */
  useEffect(() => {
    if (visitId) {
      fetchTipSettings({ visitId });
    }
  }, [visitId]);

  // authenticated is null in the initial render cycles,
  // and we intended to render nothing.
  return (
    <>
      <AppAlert />

      {authenticated && (
        <Suspense fallback={null}>
          <SideMenu
            showConfirmation={shouldShowVisitMenuItem}
            firstName={user.firstName}
            lastName={user.lastName}
          />
        </Suspense>
      )}

      {authenticated && expressOnboardingExperimentGroup !== 'treatment' && (
        <Suspense fallback={null}>
          <UserAgreementSheet />
        </Suspense>
      )}

      <CustomerRoutes userDataInitialized={userDataInitialized} />

      <BottomAppAlert />

      {isAppModalVisible && (
        <Suspense fallback={null}>
          <AppFullModal />
        </Suspense>
      )}
    </>
  );
}

export default Customer;
